import React, { useEffect, useState } from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { CalendarMonthOutlined } from "@mui/icons-material";
import { TreeItem, TreeItemProps } from "@mui/x-tree-view";
import { StopOutlined } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { FlespiVideoStreamPlayerHandle, openLiveStream } from "../../flespiVideoStreamPlayer/flespiVideoStreamPlayer";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2/useTreeItem2";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

interface CameraTreeItem {
    id: string;
    label: any;
    children?: CameraTreeItem[];
    data?: VehicleCamera;
}

interface VehicleCamera {
    deviceUnitName: string;
    channelName: string;
    channelIdx: number;
    deviceFlespiId: string;
    vehicleName: string;
    vehicleId: string;
}

export interface CustomTreeItemProps extends TreeItemProps {
    label: React.ReactNode;
    disabled?: boolean;
    playerRef: React.MutableRefObject<FlespiVideoStreamPlayerHandle>;
    onSelectCameraAndDate: (
        camData: VehicleCamera,
        itemId: string,
        date?: Dayjs) => void;
    setItemPlaying: (
        itemId: string,
        playing: boolean) => void;
    playing: boolean;
    selectedDate?: Dayjs;
    closeCurrentStream: () => void;
    isPlayback?: boolean;
    selectedCamData: (selectedCameraData: VehicleCamera) => void;
    isSelected: boolean;
}

export const CameraTreeviewCustomizer = React.forwardRef(function CustomTreeItem(
    props: CustomTreeItemProps,
    ref: React.Ref<HTMLLIElement>,
) {
    const { id, itemId, label, disabled, children, playerRef,
        isPlayback: $isPlayback,
        onSelectCameraAndDate,
        setItemPlaying,
        selectedDate,
        closeCurrentStream,
        selectedCamData,
        playing,
        isSelected,
        ...other } = props;
    const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLElement | null>(null);
    const [treeItemData, setTreeItemData] = useState<CameraTreeItem | null>(null);
    const { t } = useTranslation();

    const { publicAPI } = useTreeItem2({
        id,
        itemId,
        children,
        label,
        disabled,
        rootRef: ref,
    });

    useEffect(() => {
        setTreeItemData(publicAPI.getItem(itemId) as CameraTreeItem);
    }, [itemId]);

    const renderLabel = () => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                pr: 0.5,
                gap: 1,
            }}
        >
            {/* Label text  */}
            <Typography
                variant="body2"
                sx={{
                    flexGrow: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: treeItemData?.data ? 500 : 600,
                }}
            >
                {label}
            </Typography>
        </Box>
    );


    return (
        <>
            {treeItemData?.data ? (
                <>
                    <Box className="camera-actions"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            paddingLeft: "4px",
                            width: "100%",
                            backgroundColor: props.isSelected ? "success.light" : "transparent",
                            borderRadius: 1,
                            borderBottom: "1px dotted",
                            borderColor: "divider",
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                flexGrow: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontWeight: treeItemData?.data ? 500 : 600,
                            }}
                        >
                            {label}
                        </Typography>
                        <Box>
                            <Tooltip title={t("vehicleCameraView.liveViewTooltip")}>
                                <span>
                                    <IconButton
                                        sx={{
                                            p: 0.5,
                                        }}
                                        disabled={playing && !$isPlayback}
                                        size="small"
                                        onClick={() => {
                                            // First close existing streams from other cams
                                            closeCurrentStream();
                                            selectedCamData(treeItemData?.data);
                                            // Clear date selection
                                            onSelectCameraAndDate(
                                                treeItemData.data,
                                                itemId,
                                                null);
                                            props.setItemPlaying(itemId, true);

                                            openLiveStream(
                                                playerRef,
                                                treeItemData?.data?.deviceFlespiId,
                                                treeItemData?.data?.channelIdx
                                            );
                                        }
                                        }
                                    >
                                        <PlayArrowIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title={t("vehicleCameraView.stopVideoTooltip")}>
                                <span>
                                    <IconButton
                                        sx={{
                                            p: 0.5,
                                        }}
                                        disabled={!playing}
                                        size="small"
                                        onClick={() => {
                                            closeCurrentStream();
                                            setItemPlaying(itemId, false);
                                        }
                                        }
                                    >
                                        <StopOutlined fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title={t("vehicleCameraView.recordingsTooltip")}>
                                <IconButton
                                    sx={{
                                        p: 0.5,
                                    }}
                                    size="small"
                                    onClick={(e) => {
                                        setCalendarAnchorEl(e.currentTarget);
                                    }}
                                >
                                    <CalendarMonthOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </>
            ) : (
                <TreeItem
                    {...other}
                    ref={ref}
                    itemId={itemId}
                    id={id}
                    label={renderLabel()}
                    sx={{
                        "& .MuiTreeItem-content": {
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            minHeight: 32,
                        },

                        "& .MuiTreeItem-group": {
                            marginLeft: 1.5,
                            paddingLeft: 1,
                            borderLeft: "1px dashed",
                            borderColor: "divider",
                        },

                        "& .MuiTreeItem-content.Mui-selected": {
                            backgroundColor: "action.selected",
                        },

                        "& .MuiTreeItem-content.Mui-focused": {
                            backgroundColor: "action.hover",
                        },

                        "& .MuiTreeItem-content:hover": {
                            backgroundColor: "action.hover",
                        },
                    }}
                >
                    {children}
                </TreeItem >
            )
            }
            <Popover
                open={Boolean(calendarAnchorEl)}
                anchorEl={calendarAnchorEl}
                onClose={() => {
                    setCalendarAnchorEl(null);
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                {treeItemData?.data && (
                    <div style={{ padding: 16, minWidth: 280 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label={t("vehicleCameraView.selectDate")}
                                value={selectedDate}
                                format="DD.MM.YYYY"
                                onAccept={(date) => {
                                    onSelectCameraAndDate(
                                        treeItemData.data,
                                        itemId,
                                        date);

                                }}
                                slotProps={{
                                    textField: {
                                        inputProps: {
                                            readOnly: true
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                )}
            </Popover>
        </>
    );
});
